"use client";

import React, { useState, useEffect, useMemo } from "react";
import { Loader2 } from "lucide-react";
import { SUPPORT_EMAIL } from "@/lib/constants/support";
import type { FlutterwaveErrorCategory } from "@/lib/flutterwave_v4/error_classifier";
import { useLanguage } from "@/contexts/LanguageContext";
import { getStrings } from "@/i18n/dictionaries";

interface FlutterwaveLoadingOverlayProps {
  onRetry?: () => void;
  onSwitchToStripe?: () => void;
  onClose?: () => void;
  error?: string | null;
  errorCategory?: FlutterwaveErrorCategory | null;
}

// Progress bar configuration
const INITIAL_PROGRESS = 10;
const MAX_PROGRESS = 95;
const PROGRESS_DURATION_SECONDS = 30;
const TIMEOUT_WARNING_SECONDS = 60;

export default function FlutterwaveLoadingOverlay({
  onRetry,
  onSwitchToStripe,
  onClose,
  error = null,
  errorCategory = null,
}: FlutterwaveLoadingOverlayProps) {
  const { locale } = useLanguage();
  const t = useMemo(() => getStrings(locale, "payment"), [locale]);
  const [progress, setProgress] = useState(INITIAL_PROGRESS);
  const [dots, setDots] = useState(".");
  const [elapsed, setElapsed] = useState(0);
  const [showWarning, setShowWarning] = useState(false);

  // Animate dots
  useEffect(() => {
    const dotsInterval = setInterval(() => {
      setDots(prev => {
        if (prev === "...") return ".";
        return prev + ".";
      });
    }, 500);

    return () => clearInterval(dotsInterval);
  }, []);

  // Track elapsed time
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setElapsed(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timeInterval);
  }, []);

  // Simulated progress bar: INITIAL_PROGRESS → MAX_PROGRESS over PROGRESS_DURATION_SECONDS
  useEffect(() => {
    if (error) {
      // Don't update progress if there's an error
      return;
    }

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= MAX_PROGRESS) return MAX_PROGRESS; // Hold at max until real completion
        // Gradually increase from INITIAL_PROGRESS to MAX_PROGRESS
        const increment = (MAX_PROGRESS - INITIAL_PROGRESS) / (PROGRESS_DURATION_SECONDS * 2);
        return Math.min(prev + increment, MAX_PROGRESS);
      });
    }, 500);

    return () => clearInterval(progressInterval);
  }, [error]);

  // Show warning after timeout
  useEffect(() => {
    if (elapsed >= TIMEOUT_WARNING_SECONDS && !error) {
      setShowWarning(true);
    }
  }, [elapsed, error]);

  // ESC key to close modal when in error state
  useEffect(() => {
    if (!error) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        (onClose || onRetry)?.();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [error, onClose, onRetry]);

  if (error) {
    // ── Derive title & icon colour from error category ──
    const category = errorCategory || "gateway_error";

    const titleMap: Record<string, string> = {
      customer_error: t.payment_failed,
      gateway_error: t.payment_temp_unavailable,
      merchant_restriction: t.payment_unavailable,
    };

    const iconBgMap: Record<string, string> = {
      customer_error: "bg-red-50 hover:bg-red-100",
      gateway_error: "bg-amber-50 hover:bg-amber-100",
      merchant_restriction: "bg-slate-100 hover:bg-slate-200",
    };

    const iconColorMap: Record<string, string> = {
      customer_error: "text-red-600",
      gateway_error: "text-amber-600",
      merchant_restriction: "text-slate-500",
    };

    const ringColorMap: Record<string, string> = {
      customer_error: "focus:ring-red-300",
      gateway_error: "focus:ring-amber-300",
      merchant_restriction: "focus:ring-slate-300",
    };

    const errorTitle = titleMap[category] || titleMap.gateway_error;
    const iconBg = iconBgMap[category] || iconBgMap.gateway_error;
    const iconColor = iconColorMap[category] || iconColorMap.gateway_error;
    const ringColor = ringColorMap[category] || ringColorMap.gateway_error;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
        <div className="relative w-full max-w-md rounded-xl bg-white p-8 shadow-2xl">
          {/* Close button (top right) */}
          {(onClose || onRetry) && (
            <button
              onClick={onClose || onRetry}
              aria-label="Close"
              className="absolute right-4 top-4 z-10 pointer-events-auto rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          <div className="mb-4 flex items-center justify-center">
            <button
              onClick={onClose || onRetry}
              aria-label="Close modal"
              className={`cursor-pointer rounded-full ${iconBg} p-4 transition hover:scale-105 focus:outline-none focus:ring-2 ${ringColor}`}
            >
              {category === "gateway_error" ? (
                /* Clock / pause icon for temporary issues */
                <svg className={`h-12 w-12 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : category === "merchant_restriction" ? (
                /* Info icon for neutral unavailable */
                <svg className={`h-12 w-12 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                /* Red X for customer errors */
                <svg className={`h-12 w-12 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>

          <h2 className="mb-2 text-center text-2xl font-bold text-slate-900">
            {errorTitle}
          </h2>
          
          <p className="mb-6 text-center text-sm text-slate-600">
            {error || t.couldnt_process}
          </p>

          {/* Retry button — show for customer_error and gateway_error */}
          {onRetry && category !== "merchant_restriction" && (
            <button
              onClick={onRetry}
              className="w-full rounded-md bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 transition"
            >
              {t.try_again}
            </button>
          )}

          {/* Pay with Card (Stripe) */}
          {onSwitchToStripe && (
            <div className="mt-4 rounded-lg border-2 border-amber-200 bg-amber-50 p-4">
              <button
                onClick={onSwitchToStripe}
                className="w-full rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition"
              >
                💳 Pay with Card (Stripe)
              </button>
              <p className="mt-1.5 text-center text-xs text-amber-700">
                Use debit/credit card from any country
              </p>
            </div>
          )}

          {/* Contact Support — always shown */}
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=Payment%20Issue`}
            className="mt-4 block w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-center text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            Contact Support
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className="relative w-full max-w-md rounded-xl bg-white p-8 shadow-2xl">
        <div className="mb-6 flex items-center justify-center">
          <Loader2 className="h-16 w-16 animate-spin text-emerald-600" />
        </div>

        <h2 className="mb-2 text-center text-2xl font-bold text-slate-900">
          {t.generating_account}{dots}
        </h2>
        
        <p className="mb-6 text-center text-sm text-slate-600">
          {t.take_up_to.replace("{seconds}", String(TIMEOUT_WARNING_SECONDS))}
        </p>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full bg-emerald-600 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="mt-2 text-center text-xs text-slate-500">
            {t.pct_complete.replace("{pct}", String(Math.round(progress)))}
          </p>
        </div>

        {/* Warning after 60 seconds */}
        {showWarning && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
            <p className="text-xs text-amber-800 text-center">
              <strong>{t.still_working}</strong>
            </p>
          </div>
        )}

        {/* Elapsed time indicator */}
        <p className="mt-4 text-center text-xs text-slate-400">
          {t.elapsed.replace("{s}", String(elapsed))}
        </p>
      </div>
    </div>
  );
}
